import { useState } from 'react';

import Sidebar  from '../../component/DashboardUI/Sidebar';
import Topbar   from '../../component/DashboardUI/Topbar';
import { Badge, PulseDot } from '../../component/DashboardUI/UI';
import { ThemeProvider } from '../../hooks/useTheme';
import { JUDGE, JUDGE_NAV, QUEUE, SCORE_CRITERIA } from '../../data/mockData';
import '../../asset/css/main.css';

// Stable deterministic waveform heights — no random, no re-renders
const BARS = Array.from({ length: 60 }, (_, i) => ({
  h: Math.round(18 + Math.sin(i * 0.44) * 16 + ((i * 11 + 3) % 13)),
}));

export default function ExamineTrack() {
  return (
    <ThemeProvider>
      <PageContent />
    </ThemeProvider>
  );
}

function PageContent() {
  const [sideOpen,  setSideOpen]  = useState(false);
  const [active,    setActive]    = useState(QUEUE[0]);
  const [playing,   setPlaying]   = useState(false);
  const [scores,    setScores]    = useState({ 0:0, 1:0, 2:0, 3:0 });
  const [feedback,  setFeedback]  = useState('');
  const [submitted, setSubmitted] = useState([]);
  const [playPct,   setPlayPct]   = useState(30);

  const total = Object.values(scores).reduce((a, b) => a + b, 0) * 5;

  const selectTrack = q => { setActive(q); setScores({ 0:0,1:0,2:0,3:0 }); setFeedback(''); };

  const submitScore = () => {
    if (!total) return;
    setSubmitted(s => [...s, active.id]);
    setScores({ 0:0,1:0,2:0,3:0 });
    setFeedback('');
    const next = QUEUE.find(q => !submitted.includes(q.id) && q.id !== active.id);
    if (next) setActive(next);
  };

  const mm = Math.floor(playPct / 100 * 3);
  const ss = String(Math.floor((playPct / 100 * 210) % 60)).padStart(2, '0');

  return (
    <div className="page-shell">
      <Sidebar
        navLinks={JUDGE_NAV}
        user={JUDGE}
        isJudge={true}
        isOpen={sideOpen}
        onClose={() => setSideOpen(false)}
      />

      <div className="page-body">
        <Topbar title="Examine Tracks" onMenuClick={() => setSideOpen(o => !o)} />

        <main className="page-content anim-up">
          <div className="examine-layout">

            {/* ── Left: player + scoring ── */}
            <div className="examine-main">

              {/* Player */}
              <div className="player-card">
                <div className="player-head">
                  <img src={active.art} alt={active.title} className="player-art" />
                  <div style={{ flex:1 }}>
                    <div className="player-name">{active.title}</div>
                    <div className="player-by">by {active.artist}</div>
                    <div className="player-tags">
                      <Badge type="blue">{active.genre}</Badge>
                      <Badge type="gold">Season 4</Badge>
                      {submitted.includes(active.id) && <Badge type="green">✓ Scored</Badge>}
                    </div>
                  </div>
                </div>

                <div className="player-body">
                  {/* Waveform */}
                  <div
                    className="waveform"
                    onClick={e => {
                      const r = e.currentTarget.getBoundingClientRect();
                      setPlayPct(Math.round(((e.clientX - r.left) / r.width) * 100));
                    }}
                  >
                    {BARS.map((b, i) => (
                      <div
                        key={i}
                        className={`wv-bar ${i / 60 < playPct / 100 ? 'played' : 'idle'}`}
                        style={{ height: b.h }}
                      />
                    ))}
                  </div>

                  {/* Controls */}
                  <div className="p-ctrls">
                    <button className="p-btn">⏮</button>
                    <button className="p-btn">⏪</button>
                    <button className="p-play" onClick={() => setPlaying(p => !p)}>
                      {playing ? '⏸' : '▶'}
                    </button>
                    <button className="p-btn">⏩</button>
                    <button className="p-btn">⏭</button>
                    <span className="p-time">{mm}:{ss} / 3:30</span>
                    <button className="p-btn" style={{ marginLeft:'0.4rem' }}>🔊</button>
                  </div>
                </div>
              </div>

              {/* Scoring */}
              <div className="score-card">
                <div className="score-title">Score This Track</div>
                {SCORE_CRITERIA.map((crit, ci) => (
                  <div className="crit" key={crit}>
                    <div className="crit-row">
                      <span className="crit-name">{crit}</span>
                      <span className="crit-val">{scores[ci] * 5}/25</span>
                    </div>
                    <div className="stars">
                      {[1,2,3,4,5].map(star => (
                        <button
                          key={star}
                          className={`star ${scores[ci] >= star ? 'on' : 'off'}`}
                          onClick={() => setScores(s => ({ ...s, [ci]: star }))}
                        >★</button>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="score-divider" />
                <div className="total-row">
                  <div>
                    <div className="total-lbl">Total Score</div>
                    <div className="total-sub">Out of 100 points</div>
                  </div>
                  <div className="total-val">{total}</div>
                </div>
              </div>

              {/* Feedback */}
              <div className="fg" style={{ marginBottom: 0 }}>
                <label className="flabel">Written Feedback (optional but encouraged)</label>
                <textarea
                  className="finput gold"
                  style={{ minHeight:115 }}
                  placeholder="What stood out? What could improve? Specific production notes…"
                  value={feedback}
                  onChange={e => setFeedback(e.target.value)}
                />
              </div>

              <button
                className="btn btn-gold"
                style={{ width:'100%', padding:'0.92rem', opacity: total ? 1 : 0.45 }}
                onClick={submitScore}
                disabled={!total}
              >
                ✓ Submit Score &amp; Next Track
              </button>
            </div>

            {/* ── Right: queue ── */}
            <div className="examine-queue">
              <div className="queue-card">
                <div className="queue-head">
                  <h4>Review Queue</h4>
                  <div className="queue-counts">
                    <Badge type="red"><PulseDot /> {QUEUE.filter(q => !submitted.includes(q.id)).length} pending</Badge>
                    <Badge type="green">✓ {submitted.length} done</Badge>
                  </div>
                </div>
                <div className="queue-list">
                  {QUEUE.map(q => (
                    <div
                      key={q.id}
                      className={`q-item${active.id === q.id ? ' active' : ''}`}
                      onClick={() => selectTrack(q)}
                    >
                      <img src={q.art} alt={q.title} className="q-art" />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div className="q-name">{q.title}</div>
                        <div className="q-sub">{q.artist} · {q.genre}</div>
                      </div>
                      <div className="q-st">
                        {submitted.includes(q.id)
                          ? <Badge type="green">✓</Badge>
                          : <Badge type="red">•</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}